<template>
    <div>
        <h4 class="link-header" id="overview">Overview</h4>
        <div class="section mt-xl display-flex">
            <div class="mr-xl">
                <VDropdown
                    placement="top"
                    :triggers="['hover']"
                    :popper-triggers="['hover']"
                    :inert="false"
                >
                    <p class="bold text-moat-900 mb-xl tooltip-indicator d-inline-block">
                        Claim Breadth
                    </p>
                    <template #popper>
                        <div class="popover-container p-sm">
                            {{ t('definitionTooltips.patentClaimBreadth.definition') }}
                        </div>
                    </template>
                </VDropdown>
                <MetricSlider
                    :label="props.patentDetails?.claimBreadth"
                    :value="claimBreadthPercentage"
                    :disabled="props.patentDetails?.claimBreadth == 'Not Scored'"
                />
            </div>
            <div class="mr-xl">
                <VDropdown
                    placement="top"
                    :triggers="['hover']"
                    :popper-triggers="['hover']"
                    :inert="false"
                >
                    <p class="bold text-moat-900 mb-md tooltip-indicator d-inline-block">
                        Validity Score
                    </p>
                    <template #popper>
                        <div class="popover-container p-sm">
                            {{ t('definitionTooltips.validity.definition') }}
                        </div>
                    </template>
                </VDropdown>
                <MetricPill
                    :steps="[{ label: 'Low' }, { label: 'Med' }, { label: 'High' }]"
                    :current-value="props.patentDetails?.validity"
                    :disabled="props.patentDetails?.validity == 'Not Ranked'"
                />
            </div>
            <div class="display-flex align-items-center flex-wrap" v-if="enhancedLitigationData">
                <p class="bold text-moat-900 mb-md">Litigation Cases</p>
                <div class="w-100 stat-wrapper display-flex">
                    <div
                        class="my-sm case-stat display-flex align-items-center"
                        style="width: 120px"
                    >
                        <p class="text-moat-900 align-left mr-md">PTAB</p>
                        <AonSkeleton
                            class="w-100 display-flex align-items-center"
                            :loading="props.loadingPatentDetail"
                            :data="0"
                            :height="'20px'"
                            light
                        >
                            <p class="text-moat-900 bold">
                                {{ formatCaseCount(props.patentDetails?.itcCaseCount) }}
                            </p>
                        </AonSkeleton>
                    </div>
                    <div
                        class="my-sm case-stat display-flex align-items-center"
                        style="width: 120px"
                    >
                        <p class="text-moat-900 align-left mr-md">ITC</p>
                        <AonSkeleton
                            class="w-100 display-flex align-items-center"
                            :loading="props.loadingPatentDetail"
                            :data="0"
                            :height="'20px'"
                            light
                        >
                            <p class="text-moat-900 bold">
                                {{ formatCaseCount(props.patentDetails?.itcCaseCount) }}
                            </p>
                        </AonSkeleton>
                    </div>
                    <div
                        class="my-sm case-stat display-flex align-items-center"
                        style="width: 120px"
                    >
                        <p class="text-moat-900 align-left mr-md">DC</p>
                        <AonSkeleton
                            class="w-100 display-flex align-items-center"
                            :loading="props.loadingPatentDetail"
                            :data="0"
                            :height="'20px'"
                            light
                        >
                            <p class="text-moat-900 bold">
                                {{ formatCaseCount(props.patentDetails?.patentDcCaseCount) }}
                            </p>
                        </AonSkeleton>
                    </div>
                </div>
            </div>
        </div>

        <!-- Inventors -->
        <h6
            v-if="
                (!props.loadingPatentDetail && props.patentDetails?.inventorDetails?.length > 0) ||
                props.loadingPatentDetail
            "
            class="section-header bold text-moat-900 mb-sm mt-md"
        >
            Inventors
        </h6>
        <div class="section mt-sm display-flex flex-wrap">
            <AonSkeleton
                class="w-100 display-flex align-items-center"
                :loading="props.loadingPatentDetail"
                :data="0"
                :height="'30px'"
                light
            >
                <p class="text-moat-900" v-if="!props.patentDetails?.inventorDetails">
                    No inventors Found
                </p>
                <div v-else class="inventor-holder display-flex align-items-center flex-wrap">
                    <p v-for="inventor in props.patentDetails.inventorDetails" class="mr-sm mb-sm">
                        {{ inventor.inventorName }},
                    </p>
                </div>
            </AonSkeleton>
        </div>

        <!-- Abstract -->
        <h6 class="section-header bold text-moat-900 mb-sm mt-md">Abstract</h6>
        <div class="section mt-sm mb-xl">
            <div class="abstract-holder d-inline">
                <AonSkeleton
                    class="w-100 display-flex align-items-center"
                    :loading="props.loadingPatentDetail"
                    :data="0"
                    :height="'80px'"
                    light
                >
                    <p class="text-moat-700" v-if="!props.patentDetails?.abstract">
                        No abstract found
                    </p>
                    <p v-else-if="!showAllAbstract" class="text-stone-700 small">
                        {{ trimmedAbstract }}
                        <span class="link small" @click="showAllAbstract = !showAllAbstract">
                            Show More
                        </span>
                    </p>
                    <p v-else class="text-stone-700 small">
                        {{ props.patentDetails?.abstract }}
                        <span class="link small" @click="showAllAbstract = !showAllAbstract">
                            Show Less
                        </span>
                    </p>
                </AonSkeleton>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFlag } from '@unleash/proxy-client-vue'

import MetricSlider from '@/components/helpers/MetricSlider.vue'
import MetricPill from '@/components/helpers/MetricPill.vue'

const enhancedLitigationData = useFlag('ipAlpha.enhancedLitigationData')

const props = defineProps({
    patentDetails: {
        type: Object,
        default: () => ({}),
    },
    loadingPatentDetail: {
        type: Boolean,
        default: false,
    },
})

const { t } = useI18n()
const showAllAbstract = ref(false)

const claimBreadthPercentage = computed(() => {
    if (props.patentDetails?.claimBreadth === 'Very Broad') {
        return 100
    } else if (props.patentDetails?.claimBreadth === 'Broad') {
        return 66
    } else if (props.patentDetails?.claimBreadth === 'Average') {
        return 33
    } else if (props.patentDetails?.claimBreadth === 'Narrow') {
        return 0
    }
})

const trimmedAbstract = computed(() => {
    if (props.patentDetails?.abstract?.length > 500) {
        return `${props.patentDetails.abstract.slice(0, 500).replace(/\\n/g, ' ').trimEnd()}...`
    } else {
        return props.patentDetails?.abstract
            ? `${props.patentDetails.abstract.slice(0, 500).replace(/\\n/g, ' ').trimEnd()}`
            : ''
    }
})

const formatCaseCount = (caseCount) => {
    return (caseCount ?? 0).toLocaleString()
}
</script>

<style lang="scss" scoped>
@forward '@/styles/new_styles.scss';

.tooltip-indicator {
    cursor: help;
}

.popover-container {
    max-width: 300px;
}

.abstract-holder {
    min-height: 80px;
}

.link {
    color: var(--color-moat-900);
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
}
</style>
